import {
  IMetricAggregateInput,
  MetricAggregate,
  combineComputedAggregates,
} from '@hawkindynamics/core-client';
import {
  CollectionReference,
  Firestore,
  collection,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { collectionData } from 'rxfire/firestore';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { startOfToday } from '../../utils/date';
import { EntityType, IAggregate, IRange, IUser } from '../types';
import {
  IExclusiveDateOption,
  IInclusiveDateOption,
  convertDateOptionValue,
} from './settings/dates/dateRangeProvider';
import { authenticatedUserProvider } from './userProvider';
import { getSessionRangeTimestampsForWeights } from './utils/getSessionRangeTimestamps';

export interface IWeightAggregates {
  sessions: IAggregate[];
  combined: IAggregate;
  eventId: string;
  start: number;
  end: number;
}

/**
 * @description observes the weights for the provided athlete and date range
 * @param entityId
 * @param entityType
 * @param dateType
 * @param dateRange
 * @param afterSessionId
 */
export const weightsProvider = (
  entityId: string,
  entityType: EntityType,
  dateType: IInclusiveDateOption | IExclusiveDateOption,
  dateRange: IRange,
  afterSessionId?: number
): Observable<IWeightAggregates> => {
  return observeWeights(
    entityId,
    entityType,
    dateType,
    dateRange,
    afterSessionId
  );
};

/**
 * @description observes the weight documents
 * @param entityId
 * @param entityType
 * @param queryHash
 * @param dateType
 * @param customDateRange
 * @param afterSesssionId
 */
const observeWeights = (
  entityId: string,
  entityType: EntityType,
  dateType: IExclusiveDateOption | IInclusiveDateOption,
  customDateRange: IRange,
  afterSessionId?: number
): Observable<IWeightAggregates> => {
  return sessions({
    entityId,
    entityType,
    dateType,
    customDateRange,
    afterSessionId,
  }).pipe(
    map((aggs: IAggregate[]): IAggregate[] =>
      aggs.filter((agg: IAggregate): boolean => agg.count > 0)
    ),
    map((validSessions: IAggregate[]): IWeightAggregates => {
      const { start, end } = getSessionRangeTimestampsForWeights(validSessions);
      /// add unique id for this event emission for downstream consumers to use
      const eventId = v4();
      return {
        sessions: validSessions,
        combined:
          validSessions?.length > 0 ? combineWeights(validSessions) : null,
        eventId,
        start,
        end,
      };
    })
  );
};

interface IWeightsQuery {
  entityId: string;
  entityType: string;
  dateType: IExclusiveDateOption | IInclusiveDateOption;
  customDateRange?: IRange;
  afterSessionId?: number;
}
/**
 * @description observes the sessionsComputed collection for the given entity, queryHash, and
 * 	date range
 * @param entityId
 * @param entityType
 * @param range
 * @param afterSessionId
 */
export const sessions = ({
  entityId,
  entityType,
  dateType,
  customDateRange,
  afterSessionId,
}: IWeightsQuery): Observable<IAggregate[]> => {
  return authenticatedUserProvider().pipe(
    switchMap((user: IUser): Observable<IAggregate[]> => {
      switch (dateType.value) {
        case 'custom':
          return rangeQuery({
            firestore: user.services.firestore,
            orgId: user.orgId,
            entityType,
            entityId,
            range: customDateRange,
          });
        default:
          const numberOfSessions = convertDateOptionValue(dateType);
          return limitQuery({
            firestore: user.services.firestore,
            orgId: user.orgId,
            entityType,
            entityId,
            numberOfSessions,
            afterSessionId,
          });
      }
    })
  );
};

interface ILimitQueryArgs {
  firestore: Firestore;
  orgId: string;
  entityType: string;
  entityId: string;
  numberOfSessions: number;
  afterSessionId?: number;
}
/**
 * @description perform the query with a limit
 * @param args
 */
const limitQuery = (args: ILimitQueryArgs): Observable<IAggregate[]> => {
  const {
    orgId,
    entityId,
    entityType,
    numberOfSessions,
    afterSessionId,
    firestore,
  } = args;

  if (afterSessionId) {
    return collectionData(
      query(
        collection(
          firestore,
          `/orgs/${orgId}/${entityType}/${entityId}/weight`
        ) as CollectionReference<IAggregate>,
        orderBy('id', 'desc'),
        startAfter(afterSessionId.toString()),
        limit(numberOfSessions)
      )
    );
  } else {
    return collectionData(
      query(
        collection(
          firestore,
          `/orgs/${orgId}/${entityType}/${entityId}/weight`
        ) as CollectionReference<IAggregate>,
        orderBy('id', 'desc'),
        limit(numberOfSessions)
      )
    );
  }
};

interface IRangeQueryArgs {
  firestore: Firestore;
  orgId: string;
  entityType: string;
  entityId: string;
  range: IRange;
}

/**
 * @description performs a query with a date range
 * @param args
 */
const rangeQuery = (args: IRangeQueryArgs): Observable<IAggregate[]> => {
  const { orgId, entityId, entityType, range, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `/orgs/${orgId}/${entityType}/${entityId}/weight`
      ) as CollectionReference<IAggregate>,
      where('id', '>=', range.from.value.toString()),
      where(
        'id',
        '<=',
        range.to ? range.to.value.toString() : startOfToday().toString()
      ),
      orderBy('id', 'desc')
    )
  );
};

/**
 * @description combines the aggregates for each metric
 * @param computedAggregateMap {IComputedMetricAggregateMap}
 * @returns {ICombinedComputedAggregate}
 */
const combineWeights = (weights: IAggregate[]): IAggregate => {
  if (weights.length === 0) return null;
  return combineComputedAggregates(
    weights.map(
      (agg: IAggregate): MetricAggregate =>
        new MetricAggregate(agg as IMetricAggregateInput)
    )
  ).values();
};
